import { useCallback, useMemo, useRef, useState } from 'react';
import {
  TopHeaderMenuButton,
  Typography,
  TypographyVariants,
  TypographyColors,
  TypographyElements,
} from '@carboledger/ui';
import { ChevronDown } from '@carboledger/ui/icons';
import { useDismissOnOutside } from '@/hooks';
import { EMPTY_LIST, cx } from '@/lib/utils';
import styles from './FacilitySelector.module.scss';

const DEPTH_CLASS = [undefined, styles.nested, styles.nestedTwice];

/**
 * Props:
 *   node         one node — { id, label, value?, children? }; no `value` means a heading
 *   depth        how many headings the row sits under; each one steps it in
 *   value        the selected node's value
 *   openIds      the Set of expanded heading ids
 *   expandLabel  (label) => the expand control's accessible name, on a selectable row with children
 *   onToggle     (id) => void — a heading's row, or a selectable row's expand control
 *   onSelect     (value) => void — a selectable row
 */
const FacilityNode = ({ node, depth = 0, value, openIds, expandLabel, onToggle, onSelect }) => {
  const children = node.children ?? EMPTY_LIST;
  const isHeading = node.value == null;
  const isExpandable = isHeading || children.length > 0;
  const isOpen = openIds.has(node.id);
  const isActive = !isHeading && node.value === value;
  const rowClassName = cx(styles.row, DEPTH_CLASS[depth], isActive && styles.active);

  // The whole row is the target: a heading opens, anything else selects.
  const handleClick = useCallback(
    () => (isHeading ? onToggle(node.id) : onSelect(node.value)),
    [isHeading, onToggle, onSelect, node.id, node.value],
  );
  const handleToggle = useCallback(() => onToggle(node.id), [onToggle, node.id]);

  const label = (
    <Typography
      as={TypographyElements.span}
      variant={TypographyVariants.bodySm}
      color={TypographyColors.inherit}
      truncate
      className={styles.label}
    >
      {node.label}
    </Typography>
  );
  const chevron = (
    <ChevronDown
      size={16}
      className={cx(styles.chevron, isOpen && styles.chevronOpen)}
      aria-hidden="true"
    />
  );
  const bar = isActive ? <span className={styles.bar} aria-hidden="true" /> : null;

  return (
    <li>
      {!isHeading && isExpandable ? (
        <div className={rowClassName}>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={isActive}
            className={styles.rowSelect}
            onClick={handleClick}
          >
            {label}
          </button>
          <button
            type="button"
            aria-expanded={isOpen}
            aria-label={expandLabel?.(node.label)}
            className={styles.rowToggle}
            onClick={handleToggle}
          >
            {chevron}
          </button>
          {bar}
        </div>
      ) : (
        <button
          type="button"
          role={isHeading ? undefined : 'menuitemradio'}
          aria-checked={isHeading ? undefined : isActive}
          aria-expanded={isHeading ? isOpen : undefined}
          className={rowClassName}
          onClick={handleClick}
        >
          {label}
          {isHeading ? chevron : null}
          {bar}
        </button>
      )}
      {isExpandable && isOpen ? (
        <ul className={styles.sublist}>
          {children.map((child) => (
            <FacilityNode
              key={child.id}
              node={child}
              depth={depth + 1}
              value={value}
              openIds={openIds}
              expandLabel={expandLabel}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};

/**
 * Props:
 *   label        the trigger's text — whichever facility is in scope
 *   nodes        buildFacilityNodes(...) — a heading's facilities are its children
 *   value        the selected node's value
 *   defaultOpenIds  headings expanded on first open, so the chosen row is visible
 *   expandLabel  (label) => the expand control's accessible name, on a selectable row with children
 *   disabled     while the save is in flight
 *   onSelect     (value) => void
 */
export const FacilitySelector = ({
  label,
  nodes = EMPTY_LIST,
  value,
  defaultOpenIds = EMPTY_LIST,
  expandLabel,
  disabled = false,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [openIds, setOpenIds] = useState(() => new Set(defaultOpenIds));
  const rootRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  const handleToggleMenu = useCallback(() => setOpen((shown) => !shown), []);

  useDismissOnOutside(rootRef, open, close);

  const handleToggleGroup = useCallback((id) => {
    setOpenIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelect = useCallback(
    (next) => {
      setOpen(false);
      if (next !== value) onSelect?.(next);
    },
    [onSelect, value],
  );

  const triggerClick = useMemo(
    () => (disabled ? undefined : handleToggleMenu),
    [disabled, handleToggleMenu],
  );

  return (
    <div className={styles.root} ref={rootRef}>
      <TopHeaderMenuButton label={label} onClick={triggerClick} />
      {/* TODO: use the library Dropdown once it nests its options. */}
      {open ? (
        <ul className={styles.menu} role="menu">
          {nodes.map((node) => (
            <FacilityNode
              key={node.id}
              node={node}
              value={value}
              openIds={openIds}
              expandLabel={expandLabel}
              onToggle={handleToggleGroup}
              onSelect={handleSelect}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
};
